import { useState, type ReactNode } from 'react';
import { Link } from 'wouter';
import './InlineNudge.css';
import { isNudgeDismissed, dismissNudge } from '../upgradeNudges';

interface NudgeLink {
  label: string;
  href: string;
}

interface NudgeAction {
  label: string;
  href?: string;
  onClick?: () => void;
}

export interface InlineNudgeProps {
  /** Stable moment id used to remember dismissal for the session. */
  id: string;
  eyebrow: string;
  headline: string;
  support?: string;
  /** A brass "learn more / proof" link rendered under the support copy. */
  proofLink?: NudgeLink;
  /** Optional manifest block (e.g. the locked-lesson "what's included"). */
  children?: ReactNode;
  /** The brass-outline pill CTA. Omit for a reassurance-only nudge. */
  action?: NudgeAction;
  /** A quiet secondary link beneath the pill (e.g. a free-preview escape). */
  footerLink?: NudgeLink;
  /** Defaults to true. Pass false for gate screens with nothing behind them. */
  dismissible?: boolean;
}

/**
 * The reusable "earned moment" upgrade prompt. Calm, archival, never an
 * alert box. The PARENT decides WHEN to render it (always after value is
 * delivered); this component owns only the dismiss persistence and motion.
 */
export function InlineNudge({
  id,
  eyebrow,
  headline,
  support,
  proofLink,
  children,
  action,
  footerLink,
  dismissible = true,
}: InlineNudgeProps) {
  const [hidden, setHidden] = useState(() => dismissible && isNudgeDismissed(id));
  const [leaving, setLeaving] = useState(false);

  if (hidden) return null;

  const handleDismiss = () => {
    setLeaving(true);
    dismissNudge(id);
    // Unmount after the leave transition so no empty gap lingers.
    window.setTimeout(() => setHidden(true), 220);
  };

  return (
    <div className={`inudge${leaving ? ' inudge--leaving' : ''}`} role="note">
      {dismissible && (
        <button
          type="button"
          className="inudge__x"
          aria-label="Dismiss"
          onClick={handleDismiss}
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
      )}

      <div className="inudge__eyebrow">
        <span className="inudge__bead" aria-hidden="true" />
        <span className="inudge__eyebrow-label">{eyebrow}</span>
      </div>

      <div className="inudge__headline">{headline}</div>

      {support && <p className="inudge__support">{support}</p>}

      {proofLink && (
        <Link href={proofLink.href} className="inudge__proof">
          {proofLink.label}
        </Link>
      )}

      {children && <div className="inudge__manifest">{children}</div>}

      {action &&
        (action.href ? (
          <Link href={action.href} className="inudge__pill">
            {action.label}
          </Link>
        ) : (
          <button type="button" className="inudge__pill" onClick={action.onClick}>
            {action.label}
          </button>
        ))}

      {footerLink && (
        <Link href={footerLink.href} className="inudge__footerlink">
          {footerLink.label}
        </Link>
      )}
    </div>
  );
}
